import React from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from '../ui';
import { useResponsive } from '../../utils/ResponsiveHooks';
import './LoginSplashScreen.css';

/*
 * Splash art lives in `public/art/`, so it is referenced by RUNTIME URL and applied
 * as an inline style — the same pattern every other art-backed screen here uses
 * (`DraftPage`, `SplashPage`, `PlayerPage`, `ExpandablePlayerCard`, ...).
 *
 * It is deliberately NOT a `url()` in the stylesheet: css-loader resolves stylesheet
 * URLs as module requests at build time, so `url('/art/SplashLogin.png')` fails with
 * "Can't resolve '/art/SplashLogin.png'" (it looks relative to the CSS file, not the
 * public root). Pointing it at `../../../public/...` would compile, but would pull
 * ~3.3MB of artwork into the bundle instead of serving it as a static asset.
 *
 * `PUBLIC_URL` is honored so the app still resolves the art when served from a
 * sub-path rather than the domain root.
 */
const ART_BASE = `${process.env.PUBLIC_URL || ''}/art`;
export const SPLASH_ART_DESKTOP = `${ART_BASE}/SplashLogin.png`;
export const SPLASH_ART_MOBILE = `${ART_BASE}/SplashLogin_mobile.png`;

/*
 * Promo trailer embedded into the splash art (the empty stadium region to the
 * right of the action stack).
 *
 * `youtube-nocookie.com` is YouTube's privacy-enhanced host: it does not write
 * tracking cookies until the visitor actually presses play. This screen is shown
 * to SIGNED-OUT visitors before any consent surface exists, so the standard
 * `youtube.com/embed` host would set third-party cookies on people who have not
 * yet agreed to anything.
 *
 * Player parameters, and why each one is here:
 *   rel=0             — keep "up next" suggestions to this channel instead of
 *                       offering unrelated videos on our sign-in page.
 *   modestbranding=1  — drop the YouTube wordmark from the control bar.
 *   playsinline=1     — iOS Safari otherwise hijacks the whole screen into its
 *                       native fullscreen player and dumps the user out of the
 *                       sign-in flow when the clip ends.
 *
 * There is deliberately NO autoplay: a browser-muted autoplay clip on a login
 * screen is noise, and an unmuted one is blocked by every modern browser anyway.
 */
export const SPLASH_VIDEO_ID = 'jSZFlVBM8kg';
export const SPLASH_VIDEO_TITLE = 'Mythic Football trailer';
export const SPLASH_VIDEO_EMBED_URL =
    `https://www.youtube-nocookie.com/embed/${SPLASH_VIDEO_ID}` +
    '?rel=0&modestbranding=1&playsinline=1';

/**
 * LoginSplashScreen — the full-bleed art splash that fronts BOTH credential lanes.
 *
 * This component is deliberately PRESENTATIONAL ONLY. It renders the background art,
 * the two calls to action, and an optional popup panel. It knows nothing about Clerk,
 * legacy credentials, beta keys, or the rollout state — its two consumers own all of
 * that:
 *
 * - `ClerkAuthenticationScreen` (Clerk lane) puts Clerk's own `<SignIn>` / `<SignUp>`
 *   in the popup, which is where the "sign in with Google or other" providers come
 *   from. They are rendered by Clerk, never reimplemented here.
 * - `AuthenticationScreen` (legacy lane) puts its email/password form in the popup and
 *   routes Sign Up to the beta-key page.
 *
 * WHY THE LANES ARE NOT MERGED: the rollout matrix in `ClerkStatusBootstrap` decides
 * which credential path is permitted, and during `BETA`/`ACTIVE` the backend rejects
 * legacy login with `403 legacy_auth_unavailable`. A single screen that rendered both
 * a local password form AND Clerk providers would show at least one affordance that
 * cannot succeed. Keeping the lanes separate means each one only ever offers
 * credentials the backend will actually accept.
 *
 * ART: identical layout in both breakpoints — only the background image differs.
 * The image is chosen here in JS (public assets cannot be `url()`-ed from the
 * stylesheet — see the note above `ART_BASE`), while the button geometry lives in
 * LoginSplashScreen.css as percentages derived from the approved 1672x941 (PC) and
 * 432x941 (mobile) frames, so the stack stays locked to the logo as the viewport
 * scales. Both key off the same 768px boundary.
 */
function LoginSplashScreen({
    onSignIn,
    onSignUp,
    signInLabel = 'Sign In',
    signUpLabel = 'Sign Up',
    popupTitle = '',
    popup = null,
    isPopupOpen = false,
    onPopupClose,
    popupSize = 'sm',
    error = ''
}) {
    /*
     * Prop validation runs BEFORE any hook call, deliberately.
     *
     * These throws are wiring assertions, so if one is ever caught by an error
     * boundary that re-renders, bailing out before `useResponsive()` keeps the hook
     * call order identical on every render that gets far enough to call hooks at all.
     * Throwing between hooks is how you turn a clear wiring error into a confusing
     * "rendered fewer hooks than expected" one.
     */
    if (typeof onSignIn !== 'function') {
        throw new Error(
            `LoginSplashScreen requires an onSignIn function; received ${typeof onSignIn}.`
        );
    }
    if (typeof onSignUp !== 'function') {
        throw new Error(
            `LoginSplashScreen requires an onSignUp function; received ${typeof onSignUp}.`
        );
    }
    // A popup that cannot be dismissed traps the user on a screen with no
    // application behind it — there is no navigation chrome at this stage.
    if (isPopupOpen && typeof onPopupClose !== 'function') {
        throw new Error(
            'LoginSplashScreen requires an onPopupClose function whenever isPopupOpen is true; ' +
            `received ${typeof onPopupClose}.`
        );
    }

    /*
     * The art swap and the button geometry must agree about which frame the user is
     * looking at, so both key off the SAME 768px boundary: `useResponsive()` reports
     * mobile below 768px, and the stylesheet's `max-width: 767px` block covers exactly
     * that range. There is no width where the mobile art renders with desktop button
     * placement.
     */
    const { isMobile } = useResponsive();
    const backgroundImage = `url('${isMobile ? SPLASH_ART_MOBILE : SPLASH_ART_DESKTOP}')`;

    return (
        <main className="login-splash" aria-label="Mythic Football sign in">
            {/*
              * The STAGE carries the artwork and holds the artwork's aspect ratio,
              * scaling to fit the window. The buttons are positioned against it
              * rather than against the viewport, so the art and the action stack
              * scale as ONE unit and the buttons stay beneath the wordmark at every
              * window size. See the long comment in LoginSplashScreen.css for why a
              * plain `cover` background on a single element is wrong here (it crops
              * the logo, and the buttons then drift away from it).
              *
              * The art carries the Mythic Football logo, so the accessible name for
              * this screen comes from the `aria-label` above rather than a visible
              * heading that would double the wordmark. The stage is decorative
              * scaffolding for that art and exposes no semantics of its own.
              */}
            <div className="login-splash__stage" style={{ backgroundImage }}>
                {/*
                  * ACTIONS-THEN-TRAILER, in that DOM order, deliberately.
                  *
                  * On the narrow/portrait frame (phones, so every iOS portrait
                  * session) the stylesheet turns this wrapper into a real flex
                  * COLUMN and puts both children back in normal flow, so the
                  * trailer renders BELOW the sign-in buttons as a consequence of
                  * document order rather than of a tuned coordinate. That ordering
                  * therefore cannot be broken by a taller-than-expected stack — an
                  * error banner, a longer button label, or a large accessibility
                  * text size all just push the trailer further down instead of
                  * sliding it under the buttons.
                  *
                  * On the desktop frame the wrapper is `display: contents`, so it
                  * generates no box and both children position absolutely against
                  * the STAGE exactly as they would without it — which is what keeps
                  * them registered to the artwork as it scales. Geometry for both
                  * frames lives in LoginSplashScreen.css.
                  *
                  * This order is also the better tab order on desktop: the two
                  * calls to action come before the player.
                  */}
                <div className="login-splash__stack">
                    <div className="login-splash__actions">
                        {error && (
                            <p className="login-splash__error" role="alert">
                                {error}
                            </p>
                        )}

                        <Button
                            variant="secondary"
                            size="sm"
                            className="login-splash__button"
                            onClick={onSignIn}
                        >
                            {signInLabel}
                        </Button>

                        <Button
                            variant="primary"
                            size="sm"
                            className="login-splash__button"
                            onClick={onSignUp}
                        >
                            {signUpLabel}
                        </Button>
                    </div>

                    <div className="login-splash__video">
                        <iframe
                            className="login-splash__video-frame"
                            src={SPLASH_VIDEO_EMBED_URL}
                            title={SPLASH_VIDEO_TITLE}
                            frameBorder="0"
                            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        />
                    </div>
                </div>
            </div>

            {isPopupOpen && (
                <Modal
                    isOpen={isPopupOpen}
                    onClose={onPopupClose}
                    title={popupTitle}
                    size={popupSize}
                    className="login-splash__modal"
                >
                    {popup}
                </Modal>
            )}
        </main>
    );
}

LoginSplashScreen.propTypes = {
    /** Opens the credential popup. Required — the screen has no other sign-in path. */
    onSignIn: PropTypes.func.isRequired,

    /** Starts the sign-up/beta-admission path owned by the calling lane. */
    onSignUp: PropTypes.func.isRequired,

    signInLabel: PropTypes.string,
    signUpLabel: PropTypes.string,

    /** Accessible title for the popup. */
    popupTitle: PropTypes.string,

    /** Popup body — the lane's own credential UI. */
    popup: PropTypes.node,

    isPopupOpen: PropTypes.bool,

    /** Required whenever `isPopupOpen` is true. */
    onPopupClose: PropTypes.func,

    popupSize: PropTypes.oneOf(['sm', 'md', 'lg', 'full']),

    /** Screen-level error (e.g. a failed sign-out) shown above the buttons. */
    error: PropTypes.string
};

export default LoginSplashScreen;
